import { FC } from "react";
import { Chip } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { AnalyteType } from "../../domain/analyte.interface";

/**
 * The Active/Inactive pill — ONE component for the Table STATUS column and the cards, so the
 * two views cannot drift apart again (SIMS-9254: the cards had lost it). Text plus colour,
 * never colour alone (NFR-5).
 */
export const StatusChip: FC<{ active: boolean }> = ({ active }) => (
	<Chip
		size="small"
		data-testid="status-chip"
		label={active ? "Active" : "Inactive"}
		color={active ? "success" : "default"}
	/>
);

/**
 * The type pill's fill/ink, from the Hi-Fi's typeStyles (script L1011-1015): Stimulant on
 * --warning-light / --warning-ink, Other substance on --surface-2 / --text-secondary. The
 * design also styles Opioid and Depressant, which the app does not have, so any value it
 * does not recognise takes the neutral pair, as the design's own fallback does.
 */
export const analyteTypeChipSx = (type: string) => (theme: Theme) => {
	const stimulant = type === AnalyteType.STIMULANT;
	return {
		bgcolor: stimulant
			? theme.palette.warning.light
			: theme.palette.surfaceTwo.main,
		color: stimulant
			? theme.palette.warningInk.main
			: theme.palette.text.secondary,
		// 11px semibold, tracked, in a 22px pill (L408, L482).
		fontSize: "0.6875rem",
		fontWeight: 600,
		letterSpacing: "0.04em",
		height: "1.375rem",
		"& .MuiChip-label": { px: 1.25 },
	};
};

/** SIMS-9257 — the type as a coloured pill, in the Table TYPE column and on the cards. */
export const AnalyteTypeChip: FC<{ type?: string }> = ({ type }) => (
	<Chip
		size="small"
		data-testid="analyte-type-chip"
		label={String(type ?? "")}
		sx={analyteTypeChipSx(String(type ?? ""))}
	/>
);
